import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Draggable from "react-draggable";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  isSameDay,
  isSameWeek,
  isSameMonth,
} from "date-fns";
import enUS from "date-fns/locale/en-US";
import AddAppointments from "./AddAppointments";
import TagArrived from "./TagArrived/TagArrived";
import { Modal, ModalDialog } from "react-bootstrap";
import { toast } from "react-toastify";
import EditAppointment from "./EditAppointment";
import { useNavigate } from "react-router-dom";
import {
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaCheckDouble,
  FaRegCopy,
  FaCopy,
} from "react-icons/fa";
import Notifications from "./Notifications";
const locales = { "en-US": enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

function DraggableModal(props) {
  const nodeRef = useRef(null);
  return (
    <Modal
      {...props}
      dialogAs={(dialogProps) => (
        <Draggable
          nodeRef={nodeRef}
          cancel="input,textarea,button,select,label"
        >
          <ModalDialog {...dialogProps} ref={nodeRef} />
        </Draggable>
      )}
    />
  );
}

const Appointment = () => {
  const statuses = ["Pending", "Confirmed", "Cancelled", "Done"];
  const cardColors = {
    Pending: "bg-secondary",
    Confirmed: "bg-primary",
    Cancelled: "bg-danger",
    Done: "bg-success",
  };
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [appointments, setAppointments] = useState([]);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [serviceColors, setServiceColors] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState("All");
  const [prefillDate, setPrefillDate] = useState(null);
  const [copied, setCopied] = useState(false);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("17:00");
  const doctorOptions = [
    "All",
    ...new Set(appointments.map((a) => a.doctor_name).filter(Boolean)),
  ].map((name) => (name === "All" ? name : `Dr. ${name}`));
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState("week");

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const fetchAppointments = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/appointments.php`);
      setAppointments(res.data.appointments || []);
      formatEvents(res.data.appointments || []);
    } catch (err) {
      console.error("Failed to fetch appointments", err);
    }
  };

  const fetchPendingAppointments = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pending_appointments.php`
      );
      const raw = res.data.appointments || [];

      const normalized = raw.map((appt) => ({
        ...appt,
        date: appt.preferred_date,
        time: appt.preferred_time,
      }));

      setPendingAppointments(normalized);
    } catch (err) {
      console.error("Failed to fetch pending appointments", err);
    }
  };

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/Settings/get_time_appointments.php`
      );
      if (res.data.start_time && res.data.end_time) {
        setStartTime(res.data.start_time.slice(0, 5));
        setEndTime(res.data.end_time.slice(0, 5));
      }
    } catch (err) {
      console.error("Failed to fetch schedule", err);
    }
  };

  useEffect(() => {
    fetchAppointments();
    fetchPendingAppointments();
    fetchServiceColors();
    fetchSchedule();
  }, []);

  const buildDateWithTime = (timeStr) => {
    const [h, m] = timeStr.split(":");
    return new Date(2025, 0, 1, h, m);
  };

  const fetchServiceColors = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/service-colors.php`);
      setServiceColors(res.data);
    } catch (err) {
      console.error("Failed to fetch service colors", err);
    }
  };

  useEffect(() => {
    fetchAppointments();
    fetchPendingAppointments();
    fetchServiceColors();
  }, []);

  const formatEvents = (data) => {
    const formatted = data
      .filter((appt) => appt.status !== "Pending")
      .map((appt) => {
        const start = new Date(`${appt.date}T${appt.time}`);
        const end = new Date(`${appt.date}T${appt.end_time}`);
        return {
          id: appt.id,
          title: appt.name || "Appointment",
          start,
          end,
          status: appt.status || "Pending",
          service: appt.service || "",
          reference_number: appt.reference_number || "",
          name: appt.name || "",
          contact: appt.contact || "",
          email: appt.email || "",
          pet_name: appt.pet_name || "",
          pet_breed: appt.pet_breed || "",
          pet_species: appt.pet_species || "",
          doctor_id: appt.doctor_id || "",
          doctor_name: appt.doctor_name || "",
        };
      });
    setEvents(formatted);
  };

  const statusIcons = {
    Pending: <FaClock size={34} />,
    Confirmed: <FaCheckCircle size={34} />,
    Cancelled: <FaTimesCircle size={34} />,
    Done: <FaCheckDouble size={34} />,
  };

  const filterAppointmentsByDate = useCallback(
    (appts) => {
      return appts.filter((appt) => {
        const apptDate = new Date(appt.date);
        switch (currentView) {
          case "day":
            return isSameDay(apptDate, currentDate);
          case "week":
            return isSameWeek(apptDate, currentDate, { weekStartsOn: 1 });
          case "month":
            return isSameMonth(apptDate, currentDate);
          default:
            return true;
        }
      });
    },
    [currentView, currentDate]
  );

  const renderStatusBoxes = () => {
    const doctorFilter = (appts) => {
      if (selectedDoctor === "All") return appts;
      return appts.filter((a) => `Dr. ${a.doctor_name}` === selectedDoctor);
    };

    const counts = {
      Pending: pendingAppointments.length,
      Confirmed: doctorFilter(
        filterAppointmentsByDate(
          appointments.filter((appt) => appt.status === "Confirmed")
        )
      ).length,
      Cancelled: doctorFilter(
        filterAppointmentsByDate(
          appointments.filter((appt) => appt.status === "Cancelled")
        )
      ).length,
      Done: doctorFilter(
        filterAppointmentsByDate(
          appointments.filter((appt) => appt.status === "Done")
        )
      ).length,
    };

    return (
      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-4 g-3 mt-2 mb-4">
        {statuses.map((status, idx) => (
          <div className="col" key={idx}>
            <div
              className={`card text-white ${cardColors[status]} status-card`}
              style={{
                cursor:
                  status === "Pending" || "Confirmed" || "Cancelled" || "Done"
                    ? "pointer"
                    : "default",
                minWidth: "160px",
              }}
              onClick={() => {
                if (["Pending", "Confirmed", "Cancelled", "Done"].includes(status)) {
                  navigate(`/appointment/${status.toLowerCase()}`);
                }
              }}
            >
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0 fw-bold">{counts[status]}</h3>
                  <p className="mb-0">{status}</p>
                </div>
                <div className="ms-3">{statusIcons[status]}</div>
              </div>
            </div>
          </div>
        ))}
    </div>
    );
  };

  const eventPropGetter = (event) => {
    let backgroundColor = "#6c757d"; // default gray
    let color = "white";

    if (event.status === "Done") {
      backgroundColor = "#198754"; // green
      color = "white";
    } else if (event.status === "Cancelled") {
      backgroundColor = "#dc3545"; // red
      color = "white"; 
    } else {
      // use service color
      const firstService = (event.service || "")
        .split(",")[0]
        .trim()
        .toLowerCase();
      backgroundColor = serviceColors[firstService] || "#6c757d";
      color = "black";
    }

    let borderLeft = "none";
    if (event.status === "Arrived") {
      borderLeft = "4px solid #198754";
    }

    return {
      style: {
        backgroundColor,
        color,
        borderRadius: "5px",
        padding: "2px",
        border: "none",
        borderLeft, 
      },
    };
  };

  const handleDeleteAppointment = async () => {
    try {
      const userId = localStorage.getItem("userID");
      const userEmail = localStorage.getItem("userEmail");

      await axios.delete(`${API_BASE_URL}/api/appointments.php`, {
        data: {
          id: selectedEvent.id,
          user_id: userId,
          name: selectedEvent.name,
          user_email: userEmail,
        },
      });

      toast.success("Appointment deleted successfully!");
      setShowEventModal(false);
      setShowDeleteConfirm(false);
      fetchAppointments();
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Failed to delete appointment. Please try again.");
    }
  };

  const filteredEvents =
    selectedDoctor === "All"
      ? events
      : events.filter((e) => `Dr. ${e.doctor_name}` === selectedDoctor);

  return (
    <div className="container mt-2">
      <div className="container d-flex align-items-center justify-content-between p-0">
        <h1 style={{ fontWeight: "bold" }}>Appointments</h1>
        <Notifications />
      </div>
      {renderStatusBoxes()}

      <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
        <div>
          <select
            className="form-select w-auto"
            value={selectedDoctor}
            onChange={(e) => setSelectedDoctor(e.target.value)}
          >
            {doctorOptions.map((doc, index) => (
              <option key={index} value={doc}>
                {doc}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-primary me-2 btn-gradient"
            onClick={() => setShowTagModal(true)}
          >
            Tag as Arrived
          </button>

          <button
            className="btn btn-primary btn-gradient"
            onClick={() => setShowModal(true)}
          >
            Add Appointment
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body" style={{ height: "80vh" }}>
          <Calendar
            localizer={localizer}
            events={filteredEvents}
            startAccessor="start"
            endAccessor="end"
            defaultView="week"
            views={["month", "week", "day"]}
            step={30}
            timeslots={2}
            selectable
            min={buildDateWithTime(startTime)}
            max={buildDateWithTime(endTime)}
            style={{ height: "100%" }}
            messages={{
              noEventsInRange: "No appointments to show.",
            }}
            eventPropGetter={eventPropGetter}
            onSelectEvent={handleEventClick}
            onSelectSlot={(slotInfo) => {
              const selectedTime = format(slotInfo.start, "HH:mm");
              const safeTime =
                selectedTime === "00:00" ? startTime : selectedTime;

              setShowModal(true);
              setPrefillDate({
                date: format(slotInfo.start, "yyyy-MM-dd"),
                time: safeTime,
              });
            }}
            onNavigate={(newDate) => setCurrentDate(newDate)}
            onView={(newView) => setCurrentView(newView)}
            date={currentDate}
            view={currentView}
          />
        </div>
      </div>

      {showModal && (
        <DraggableModal
          show={showModal}
          onHide={() => setShowModal(false)}
          size="md"
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Appointment</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <AddAppointments
              prefill={{
                date: prefillDate?.date || "",
                time: prefillDate?.time || "",
              }}
              onClose={() => {
                setShowModal(false);
                setPrefillDate(null);
                fetchAppointments();
              }}
            />
          </Modal.Body>
        </DraggableModal>
      )}

      {showTagModal && (
        <Modal
          show={showTagModal}
          onHide={() => setShowTagModal(false)}
          size="md"
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Tag Arrived</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <TagArrived
              onClose={() => {
                setShowTagModal(false);
                fetchAppointments();
              }}
            />
          </Modal.Body>
        </Modal>
      )}

      {selectedEvent && (
        <Modal show={showEventModal} onHide={() => setShowEventModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Appointment Info</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Personal Details */}
            <section className="mb-3">
              <h6 className="text-primary border-bottom pb-2">
                Personal Details
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <p className="text-break">
                    <strong>Name:</strong> {selectedEvent.name}
                  </p>
                  <p>
                    <strong>Contact #:</strong> {selectedEvent.contact}
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="text-break">
                    <strong>Email:</strong> {selectedEvent.email || "N/A"}
                  </p>
                </div>
              </div>
            </section>

            {/* Patient Details */}
            <section className="mb-3">
              <h6 className="text-primary border-bottom pb-2">
                Patient Details
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <p>
                    <strong>Pet Name:</strong> {selectedEvent.pet_name}
                  </p>
                  <p>
                    <strong>Breed:</strong> {selectedEvent.pet_breed}
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Species:</strong> {selectedEvent.pet_species}
                  </p>
                </div>
              </div>
            </section>

            {/* Service */}
            <section className="mb-4">
              <h6 className="text-primary border-bottom pb-2">Service</h6>
              <p>{selectedEvent.service || "—"}</p>
            </section>

            {/* Appointment Details */}
            <section>
              <h6 className="text-primary border-bottom pb-2">
                Appointment Details
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <p className="d-flex align-items-center gap-2">
                    <strong>Ref #:</strong> {selectedEvent.reference_number || "—"}
                    {selectedEvent.reference_number && (
                      <>
                        {copied ? (
                          <FaRegCopy
                            style={{ cursor: "pointer", transition: "color 0.2s" }}
                            size={14}
                            title="Copied!"
                          />
                        ) : (
                          <FaCopy
                            style={{ cursor: "pointer", color: "black" }}
                            size={14}
                            title="Copy reference number"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedEvent.reference_number);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 1000);
                            }}
                          />
                        )}
                      </>
                    )}
                  </p>
                  <p>
                    <strong>Date:</strong>{" "}
                    {selectedEvent.start
                      ? format(selectedEvent.start, "MMMM dd, yyyy")
                      : "—"}
                  </p>
                  <p>
                    <strong>Time:</strong>{" "}
                    {selectedEvent.start && selectedEvent.end
                      ? `${format(selectedEvent.start, "hh:mm a")} to ${format(
                          selectedEvent.end,
                          "hh:mm a"
                        )}`
                      : "—"}
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Doctor:</strong>{" "}
                    {selectedEvent.doctor_name || "TBD"}
                  </p>
                  <p>
                    <strong>Status:</strong> {selectedEvent.status || "Pending"}
                  </p>
                </div>
              </div>
            </section>
          </Modal.Body>
          <Modal.Footer>
            {selectedEvent?.status === "Cancelled" && (
              <button
                className="btn btn-danger me-auto"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => setShowEventModal(false)}
            >
              Close
            </button>

            {selectedEvent.status !== "Done" && (
              <button
                className="btn btn-primary me-2"
                onClick={() => {
                  setShowEditModal(true);
                  setShowEventModal(false);
                }}
              >
                Edit
              </button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {selectedEvent && (
        <EditAppointment
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          eventData={selectedEvent}
          onUpdated={fetchAppointments}
        />
      )}

      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this appointment?</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleDeleteAppointment}>
            Yes, Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Appointment;
